import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import RequireAuth from "@/components/RequireAuth";
import ErrorBoundary from "@/components/ErrorBoundary";
import AccessibilityPanel from "@/components/accessibility/AccessibilityPanel";
import SkipToContent from "@/components/accessibility/SkipToContent";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import TextAnalysis from "./pages/TextAnalysis";
import ImageAnalysis from "./pages/ImageAnalysis";
import VideoAnalysis from "./pages/VideoAnalysis";
import VoiceAnalysis from "./pages/VoiceAnalysis";
import DocumentAnalysis from "./pages/DocumentAnalysis";
import PlagiarismCheck from "./pages/PlagiarismCheck";
import ProxyInterview from "./pages/ProxyInterview";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Unauthorized from "./pages/Unauthorized";
import Admin from "./pages/Admin";
import AuditLogs from "./pages/AuditLogs";
import ApiCredentials from "./pages/ApiCredentials";
import Layout from "./components/Layout";
import NotFound from "./pages/NotFound";
import AdminPromotion from "./pages/AdminPromotion";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DataRequest from "./pages/DataRequest";
import PrivacyPreferences from "./pages/PrivacyPreferences";
import CookieConsent from "./components/CookieConsent";
import Contact from "./pages/Contact";
import Documentation from "./pages/Documentation";
import MLTraining from './pages/MLTraining';
import { queryClient } from "./lib/queryClient";
import { useEffect } from "react";
import { captureMessage } from "./utils/monitoringService";
import { initializeAppMonitoring } from "./utils/monitoringExamples";
import TextAnalysisAccuracy from './routes/testing/TextAnalysisAccuracy';

const App = () => {
  useEffect(() => {
    initializeAppMonitoring().catch(err => {
      console.error("Failed to initialize monitoring:", err);
    });
    
    captureMessage("Application started", "info", {
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV
    });
  }, []);

  return (
    <ErrorBoundary component="AppRoot">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AccessibilityProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <SkipToContent />
                <CookieConsent />
                <AccessibilityPanel />
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/data-request" element={<DataRequest />} />
                  <Route path="/privacy-preferences" element={<PrivacyPreferences />} />
                  <Route path="/contact" element={<Contact />} />
                  
                  <Route element={<RequireAuth />}>
                    <Route element={<Layout />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                    </Route>
                  </Route>

                  <Route element={<RequireAuth requiredPermissions={['view_analysis']} />}>
                    <Route element={<Layout />}>
                      <Route path="/text-analysis" element={<TextAnalysis />} />
                      <Route path="/image-analysis" element={<ImageAnalysis />} />
                      <Route path="/video-analysis" element={<VideoAnalysis />} />
                      <Route path="/voice-analysis" element={<VoiceAnalysis />} />
                      <Route path="/document-analysis" element={<DocumentAnalysis />} />
                      <Route path="/plagiarism-check" element={<PlagiarismCheck />} />
                      <Route path="/proxy-interview" element={<ProxyInterview />} />
                    </Route>
                  </Route>

                  <Route element={<RequireAuth requiredPermissions={['manage_api_keys']} />}>
                    <Route element={<Layout />}>
                      <Route path="/api-credentials" element={<ApiCredentials />} />
                    </Route>
                  </Route>
                  
                  <Route element={<RequireAuth requiredPermissions={['access_admin_panel']} />}>
                    <Route element={<Layout />}>
                      <Route path="/admin" element={<Admin />} />
                    </Route>
                  </Route>

                  <Route element={<RequireAuth requiredPermissions={['view_audit_logs']} />}>
                    <Route element={<Layout />}>
                      <Route path="/audit-logs" element={<AuditLogs />} />
                    </Route>
                  </Route>
                  
                  <Route element={<RequireAuth allowedRoles={["admin", "analyst"]} />}>
                    <Route element={<Layout />}>
                      <Route path="/documentation" element={<Documentation />} />
                    </Route>
                  </Route>
                  
                  <Route element={<RequireAuth allowedRoles={["admin"]} />}>
                    <Route path="/promote-admin" element={<AdminPromotion />} />
                  </Route>
                  
                  <Route element={<RequireAuth allowedRoles={["admin"]} />}>
                    <Route path="/ml-training" element={<MLTraining />} />
                  </Route>
                  
                  <Route path="/testing/accuracy" element={<TextAnalysisAccuracy />} />
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AuthProvider>
            </BrowserRouter>
          </AccessibilityProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
